import React, { useState, forwardRef, useEffect, useRef } from 'react';
import { View, Text, TextInput, Pressable, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { Button } from '../ui/Button';
import { addColorOpacity } from '../../shared/utils/styles';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../../shared/utils/logger';

interface CommentInputProps {
  onSubmit: (content: string) => Promise<void>;
  placeholder?: string;
  initialValue?: string;
  onCancel?: () => void;
  submitLabel?: string;
  parentAuthor?: string;
  inputId?: string; // Unique ID for scrolling to this input
}

export const CommentInput = forwardRef<View, CommentInputProps>(({
  onSubmit,
  placeholder = 'Write a comment...',
  initialValue = '',
  onCancel,
  submitLabel = 'Post',
  parentAuthor,
  inputId,
}, ref) => {
  const styles = useStyles();
  const responsive = useResponsive();
  const { isMobile, isSmallMobile } = responsive;
  
  const [content, setContent] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasScrolledRef = useRef(false);

  // Scroll to this input when it mounts with a threadId from URL
  useEffect(() => {
    if (Platform.OS === 'web' && inputId && !hasScrolledRef.current) {
      // Extract threadId from inputId (format: "comment-input-{threadId}")
      const threadIdMatch = inputId.match(/^comment-input-(\d+)$/);
      if (threadIdMatch) {
        const threadId = threadIdMatch[1];
        // Check if URL has matching threadId
        const urlParams = new URLSearchParams(window.location.search);
        const urlThreadId = urlParams.get('threadId');
        
        if (urlThreadId === threadId) {
          // Wait for layout, then scroll
          const timer = setTimeout(() => {
            const element = document.getElementById(inputId);
            if (element) {
              const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: elementTop - 150, behavior: 'smooth' });
              hasScrolledRef.current = true;
            }
          }, 500);
          
          return () => clearTimeout(timer);
        }
      }
    }
  }, [inputId]);

  const handleSubmit = async (e?: any) => {
    // Prevent form submission/page refresh
    if (e?.preventDefault) {
      e.preventDefault();
    }
    
    const trimmed = content.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(trimmed);
      // Clear content only after successful submission
      setContent('');
    } catch (error: any) {
      logger.error('Error submitting comment', error, 'comments');
      // Error is handled in parent component, but we should still reset submitting state
      // Don't clear content if there was an error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View
      ref={ref}
      {...(Platform.OS === 'web' && inputId ? { id: inputId } : {})}
      data-comment-input={Platform.OS === 'web' ? 'true' : undefined}
      testID={inputId}
      style={{
        padding: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
        backgroundColor: styles.tokens.color.surface,
      }}
    >
      {parentAuthor && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: styles.utils.spacing.xs,
            paddingHorizontal: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
            paddingVertical: isSmallMobile ? 2 : 4,
            backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.05),
            borderRadius: styles.utils.radius.sm,
          }}
        >
          <Ionicons
            name="return-down-forward"
            size={isSmallMobile ? 12 : 14}
            color={styles.tokens.color.primary}
            style={{ marginRight: styles.utils.spacing.xs }}
          />
          <Text
            style={{
              fontSize: isSmallMobile ? 10 : styles.utils.typography.sizes.xs,
              color: styles.tokens.color.primary,
              fontFamily: styles.utils.typography.body.fontFamily,
              fontWeight: styles.utils.typography.weights.medium,
            }}
            numberOfLines={1}
          >
            Replying to {parentAuthor.split('@')[0]}
          </Text>
        </View>
      )}
      
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
          borderWidth: 1,
          borderColor: styles.tokens.color.border,
          borderRadius: styles.utils.radius.md,
          backgroundColor: styles.tokens.color.background,
          paddingHorizontal: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
          paddingVertical: isSmallMobile ? 2 : styles.utils.spacing.xs,
        }}
      >
        <TextInput
          style={{
            flex: 1,
            fontSize: isSmallMobile ? styles.utils.typography.sizes.sm : styles.utils.typography.sizes.base,
            color: styles.tokens.color.onSurface,
            fontFamily: styles.utils.typography.body.fontFamily,
            minHeight: isSmallMobile ? 32 : 36,
            maxHeight: isSmallMobile ? 80 : 120,
            paddingVertical: isSmallMobile ? 2 : styles.utils.spacing.xs,
            textAlignVertical: 'top',
          }}
          value={content}
          onChangeText={setContent}
          placeholder={placeholder}
          placeholderTextColor={addColorOpacity(styles.tokens.color.onSurface, 0.5)}
          multiline
          maxLength={10000}
          spellCheck={true}
          autoCorrect={true}
          autoCapitalize="sentences"
        />
        
        <View style={{ flexDirection: 'row', gap: isSmallMobile ? 2 : styles.utils.spacing.xs, alignItems: 'center' }}>
          {onCancel && (
            <Pressable
              onPress={onCancel}
              style={{
                paddingHorizontal: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
                paddingVertical: isSmallMobile ? 2 : styles.utils.spacing.xs,
              }}
            >
              <Text
                style={{
                  fontSize: isSmallMobile ? styles.utils.typography.sizes.xs : styles.utils.typography.sizes.sm,
                  color: addColorOpacity(styles.tokens.color.onSurface, 0.7),
                  fontFamily: styles.utils.typography.body.fontFamily,
                  fontWeight: styles.utils.typography.weights.medium,
                }}
              >
                Cancel
              </Text>
            </Pressable>
          )}
          
          <Pressable
            onPress={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            style={{
              paddingHorizontal: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
              paddingVertical: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
              backgroundColor:
                !content.trim() || isSubmitting
                  ? addColorOpacity(styles.tokens.color.primary, 0.3)
                  : styles.tokens.color.primary,
              borderRadius: styles.utils.radius.md,
              opacity: !content.trim() || isSubmitting ? 0.6 : 1,
            }}
          >
            <Text
              style={{
                fontSize: isSmallMobile ? styles.utils.typography.sizes.xs : styles.utils.typography.sizes.sm,
                color: styles.tokens.color.onPrimary,
                fontFamily: styles.utils.typography.body.fontFamily,
                fontWeight: styles.utils.typography.weights.semibold,
              }}
            >
              {submitLabel}
            </Text>
          </Pressable>
        </View>
      </View>
      
      {content.length > 0 && !isSmallMobile && (
        <Text
          style={{
            fontSize: styles.utils.typography.sizes.xs,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
            textAlign: 'right',
            marginTop: styles.utils.spacing.xs,
            fontFamily: styles.utils.typography.body.fontFamily,
          }}
        >
          {content.length.toLocaleString()} / 10,000
        </Text>
      )}
    </View>
  );
});

CommentInput.displayName = 'CommentInput';

